import { I18n } from "@assets/strings";
import { List } from "immutable";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useOrganizationMembers } from "@domain/user/hooks";
import { Person } from "@domain/user/user";
import { ClosableModal } from "@ui/modals/closableModal";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { PersonSelector } from "@ui/components/inputs/personSelector";

interface TransferOwnershipModalProps extends React.HTMLAttributes<HTMLDivElement> {
	organizationId: string;
	close: () => void;
}

export const TransferOwnershipModal: React.FC<TransferOwnershipModalProps> = ({
	organizationId,
	close,
	...htmlProps
}) => {
	const currentProfile = useCurrentProfile();
	const [chosenAdmin, setChosenAdmin] = useState<Person | undefined>(currentProfile ?? undefined);
	const { result: admins, loading } = useOrganizationMembers(organizationId, "ADMINISTRATOR");
	const askConfirmation = useConfirmation(
		I18n.t("organization.transfer_ownership.title"),
		I18n.t("confirm"),
		I18n.t("organization.transfer_ownership.confirmation")
	);
	const { userService } = useServices();

	return (
		<Container
			{...htmlProps}
			close={close}
			title={I18n.t("organization.transfer_ownership.title")}
			keyboardEventDisabled
		>
			<Description>{I18n.t("organization.transfer_ownership.selection")}</Description>
			{currentProfile && chosenAdmin ? (
				<>
					<PersonSelector
						current={chosenAdmin.id !== currentProfile.id ? chosenAdmin : undefined}
						items={admins.filter((admin) => admin.id !== currentProfile.id)}
						onItemChange={setChosenAdmin}
					/>
					<Buttons>
						<SecondaryButton onClick={close}>{I18n.t("cancel")}</SecondaryButton>
						<LoadingButton
							loading={loading}
							onClick={() =>
								askConfirmation(async () => {
									try {
										await userService.addMembersToOrganization(
											organizationId,
											List([chosenAdmin.id]),
											"SUPER_ADMINISTRATOR"
										);
										close();
										createToast(I18n.t("success"), I18n.t("members.SUPER_ADMINISTRATOR.add.success"), "success");
									} catch {
										createToast(I18n.t("error"), I18n.t("members.add.error"), "error");
									}
								})
							}
						>
							{I18n.t("validate")}
						</LoadingButton>
					</Buttons>
				</>
			) : loading ? (
				<ClipLoader />
			) : null}
		</Container>
	);
};

const Container = styled(ClosableModal)`
	align-items: stretch;
	${gap(35)}
`;

const Description = styled.div`
	${({ theme }) => theme.textStyles.primary};
`;

const Buttons = styled.div`
	align-self: center;
	display: flex;
	align-items: center;
	${gap(24, "row")};
`;
